import type { FlagKind } from 'definitions';
import type { Flag, FlagListItem } from './flag-types';
import type { FlagStatusLabel } from './flag-status';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { can } from 'definitions/capabilities';
import { useAuth } from 'hooks/useAuth';
import { useClient } from 'hooks/useClient';
import { useQueryState } from 'hooks/useQueryState';
import { useWebsites } from '@/state/useWebsites';
import { FLAG_STATUS_LABELS, FLAG_STATUS_ORDER, flagStatus, nextStep, stepProgress } from './flag-status';

export const FLAGS_KEY = 'FLAGS';
export const FLAG_KEY = 'FLAG';

/** The list's views: All is every flag that is not archived; the rest are the statuses. */
export const FLAG_VIEWS = ['All', ...FLAG_STATUS_LABELS] as const;
export type FlagView = (typeof FLAG_VIEWS)[number];

/** One row of the list, with everything the columns show and sort on worked out once. */
export type FlagRow = FlagListItem & {
  statusLabel: FlagStatusLabel;
  statusOrder: number;
  progress: string | null;
  kind: FlagKind;
  rolloutBp: number;
  next: string | null;
  /** The later of the flag's and its rollout's last change. */
  changedAt: number;
};

/**
 * The list and a flag's page are refreshed this often while open, so "50% in 18h" counts down
 * and a step the scheduler took shows up.
 */
export const FLAG_POLL_MS = 60_000;

export function toFlagRow(flag: FlagListItem, now: number): FlagRow {
  const statusLabel = flagStatus(flag);
  return {
    ...flag,
    statusLabel,
    statusOrder: FLAG_STATUS_ORDER[statusLabel],
    progress: stepProgress(flag),
    kind: flag.flagRollout?.kind ?? 'code',
    rolloutBp: flag.flagRollout?.rolloutBp ?? 0,
    next: nextStep({ flag, now }),
    changedAt: Math.max(flag.updatedAt ?? 0, flag.flagRollout?.updatedAt ?? 0),
  };
}

const inView = (row: FlagRow, view: FlagView) =>
  view === 'All' ? row.statusLabel !== 'Archived' : row.statusLabel === view;

export type CreateFlagInput = {
  name: string;
  description?: string;
  flagKey: string;
  kind: FlagKind;
  bucketOn: 'visitor' | 'customer_id';
  offPayload?: unknown;
  onPayload?: unknown;
};

/**
 * The Feature flags list for the website picked in the header: the rows, the status views with
 * their counts, and creating a flag. Like the Library, isLoading and isError leave the hook,
 * since a disabled query (no website) looks the same as an empty list from the data alone.
 */
export const useFlagsList = () => {
  const [view, setView] = useQueryState<FlagView>('view', 'All');
  const client = useClient();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { website } = useWebsites();
  const websiteId = website?.id ?? null;

  const { data, isLoading, isError, refetch } = useQuery<FlagListItem[]>({
    queryKey: [FLAGS_KEY, websiteId],
    enabled: !!websiteId,
    queryFn: () => client.get<FlagListItem[]>(`/flags/website/${websiteId}`),
    refetchInterval: FLAG_POLL_MS,
  });

  const now = Date.now();
  const allRows = (data ?? []).map((flag) => toFlagRow(flag, now));
  const validView: FlagView = (FLAG_VIEWS as readonly string[]).includes(view) ? view : 'All';
  const rows = allRows.filter((row) => inView(row, validView));

  const counts = Object.fromEntries(
    FLAG_VIEWS.map((option) => [option, allRows.filter((row) => inView(row, option)).length]),
  ) as Record<FlagView, number>;
  // A pill for each status some flag is in, so a short list does not carry seven empty filters.
  const views = FLAG_VIEWS.filter((option) => option === 'All' || option === validView || counts[option] > 0);

  const createFlag = async (input: CreateFlagInput): Promise<Flag> => {
    if (!websiteId) throw new Error('Select a website in the header first.');
    const flag = await client.post<Flag>('/flags', { ...input, websiteId });
    await queryClient.invalidateQueries({ queryKey: [FLAGS_KEY] });
    return flag;
  };

  return {
    websiteId,
    hasWebsite: !!websiteId,
    isLoading,
    isError,
    refetch: () => void refetch(),
    totalCount: allRows.length,
    rows,
    view: validView,
    setView,
    views,
    counts,
    // POST /flags needs experiments:create; a Member does not see the button.
    canCreate: can(user, 'experiments:create'),
    createFlag,
  };
};

export type FlagsListManager = ReturnType<typeof useFlagsList>;
